import { useNavigate, useParams } from 'react-router-dom';
import {
  checkRevisionAnswer,
  finishRevision,
  getRevision,
} from '../../queries/Revision';
import { useGenericQuery } from '../../../../core/api/reactQuery';
import { ExerciseAction, IExercise } from '../../../../core/models/exercise';
import { useEffect, useState } from 'react';

export const useRevisionExercises = () => {
  const { id, slug } = useParams();
  const [exercises, setExercises] = useState<IExercise[]>([]);
  const [subject, setSubject] = useState<string>();
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [isRight, setIsRight] = useState<boolean>(false);
  const [isFinishingRevision, setIsFinishingRevision] =
    useState<boolean>(false);

  const navigate = useNavigate();

  const { data, isPending } = useGenericQuery(
    ['fetch-course-revision', id],
    () => getRevision(id || ''),
    () => {}
  );

  useEffect(() => {
    if (!isPending) {
      setExercises(organizeExercisesFields(data?.exercises));
      setSubject(data?.subject);
    }
  }, [data]);

  useEffect(() => {
    if(isRight) {
      setTimeout(function () {
        setIsRight(false)
      }, 7000);
    }
  }, [isRight])


  const organizeExercisesFields = (exercises: IExercise[] | undefined) => {
    if (!exercises) {
      return [];
    }

    const organizedExercises = exercises.map((exercise) => {
      if (exercise.alternatives) {
        exercise.alternatives = exercise.alternatives.map((alternative) => {
          alternative.id = alternative._id;

          alternative.status = alternative.selected
            ? exercise.status === 'correct'
              ? 'success'
              : 'error'
            : 'default';

          return alternative;
        });
      }

      exercise.id = exercise._id;
      exercise.done = exercise.answered;
      exercise.hasAlternativeSelected = exercise.answered;
      exercise.correct = exercise.status === 'correct';
      exercise.exerciseType = exercise.exercise_type;

      return exercise;
    });

    organizedExercises[0].selected = true;

    return organizedExercises;
  };

  const changeExercise = (page: number) => {
    const updatedExercises = exercises.map((exercise: IExercise) => {
      exercise.selected = exercise.id === exercises[page].id;
      return exercise;
    });

    setExercises(updatedExercises);
  };

  const getSelectedAlternatives = (exercise: IExercise) => {
    const selectedAlternativesIds = exercise.alternatives
      .filter((alternative) => alternative.selected)
      .map((alternative) => alternative.id);

    return selectedAlternativesIds;
  };

  const updateExercise = async (
    exercise: IExercise,
    action?: ExerciseAction
  ) => {
    const updatedExercises = [...exercises];

    const existingExerciseIndex = updatedExercises.findIndex(
      (stateExercise) => stateExercise.id === exercise.id
    );

    updatedExercises[existingExerciseIndex] = exercise;

    setExercises(updatedExercises);

    if (action === 'verifyAnswer') {
      const selectedAlternativesIds = getSelectedAlternatives(exercise);

      if(exercise.correct) {
        setIsRight(true)
      }

      await checkRevisionAnswer(id || '', {
        answer: selectedAlternativesIds,
        correct: exercise.correct,
        question: exercise.id,
      });
    }
  };

  const handleFinishRevision = async (forceNewDate?: boolean) => {
    if (isFinishingRevision) {
      return;
    }

    setIsFinishingRevision(true);

    try {
      await finishRevision(id || '', forceNewDate);

      navigate(`/cursos/${slug}/revisao/`);
    } finally {
      setIsFinishingRevision(false);
    }
  };

  const totalCorrectAnswers = exercises.filter(
    (exercise) => exercise?.correct
  ).length;

  return {
    subject,
    exercises,
    isPending,
    totalCorrectAnswers,
    showConfirmationModal,
    isRight,
    isFinishingRevision,
    changeExercise,
    updateExercise,
    handleFinishRevision,
    setShowConfirmationModal,
  };
};
